import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Alert,
  Skeleton,
  LinearProgress,
} from '@mui/material';
import { Refresh, Search } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchUsageMonitoring } from '../../../store/slices/superAdminSlice';

const UsageMonitoringPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { usageMonitoring, loading, error } = useSelector((state: RootState) => state.superAdmin);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(() => {
    dispatch(fetchUsageMonitoring({ searchTerm: searchTerm || undefined }));
  }, [dispatch, searchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Usage Monitoring</Typography>
          <Typography variant="body2" color="text.secondary">Monitor tenant resource usage</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Search by company" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ endAdornment: <Search color="action" /> }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button fullWidth variant="outlined" onClick={fetchData}>Search</Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Employees</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Usage</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Attendance</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Leave Requests</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Payroll Runs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
            ) : usageMonitoring.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No data found</Typography></TableCell></TableRow>
            ) : (
              usageMonitoring.map((item) => {
                const usagePercent = item.employeeLimit > 0 ? (item.employeeCount / item.employeeLimit) * 100 : 0;
                return (
                  <TableRow key={item.tenantId} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{item.tenantName}</Typography></TableCell>
                    <TableCell>{item.employeeCount} / {item.employeeLimit}</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(usagePercent, 100)}
                        color={usagePercent > 90 ? 'error' : usagePercent > 70 ? 'warning' : 'primary'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">{usagePercent.toFixed(0)}%</Typography>
                    </TableCell>
                    <TableCell>{item.attendanceRecords.toLocaleString()}</TableCell>
                    <TableCell>{item.leaveRequests.toLocaleString()}</TableCell>
                    <TableCell>{item.payrollRuns}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UsageMonitoringPage;
